import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { useImages } from '../context/ImageContext';

interface Review {
  id: string;
  title: string;
  author: string;
  product: string;
  rating: number;
  text: string;
  createdAt: string;
}

const ComparePage: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const { getImage } = useImages();

  // Fetch top 3 reviews
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
        const response = await fetch(`${API_URL}/api/reviews?sortBy=newest&limit=3`);
        const data = await response.json();
        setReviews(data.reviews || []);
      } catch (error) {
        console.error('Error fetching reviews:', error);
      }
    };
    fetchReviews();
  }, []);

  const renderStars = (rating: number) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  return (
    <div className="compare-page">
      {/* Hero Section */}
      <section className="compare-hero-new">
        <div className="compare-hero-container">
          <div className="compare-hero-image">
            <div className="circular-image-wrapper">
              {getImage('1704WhereItWorksCouple.jpg') && (
                <img 
                  src={getImage('1704WhereItWorksCouple.jpg')} 
                  alt="Happy couple" 
                  className="circular-image"
                />
              )}
            </div>
          </div>
          
          <div className="compare-hero-content">
            <h1>Alert1 vs Life Alert Cost:</h1>
            <h2 className="compare-hero-subtitle">No Contracts, Free Equipment, & Save BIG with our Annual Plans!</h2>
            
            <p className="compare-hero-text">
              Comparing Life Alert to Alert1? If you are choosing a medical alert provider and want the best price 
              with 24/7/365 monitoring you can trust, the clear choice is Alert1.
            </p>
            
            <p className="compare-hero-text">
              Whether you are looking for a device with fall detection, a system that works on-the-go with built-in GPS, 
              or a combination of in-home and mobile solutions, we have a variety of alert systems to suit your needs.
            </p>
            
            <p className="compare-hero-text">
              If you're looking for a less expensive alternative to Life Alert, you are not alone. Many seniors and their 
              families call and tell us they are looking for a more affordable medical alert system after calling Life Alert. 
              Life Alert medical alerts may be <strong>over three times the price</strong> of a comparable Alert1 system.<sup>1</sup> 
              Alert1 offers the latest medical alert systems for the best price. We are confident you will find Alert1 has 
              transparent, affordable pricing and industry-leading customer service. We have been keeping our members safe 
              and independent for 37 years!
            </p>
            
            <a href="/medical-alerts" className="btn btn-primary compare-hero-btn">VIEW SYSTEMS</a>
          </div>
        </div>
      </section>

      {/* Main Comparison Table */}
      <section className="comparison-section">
        <div className="container">
          <h2>Alert1 vs Life Alert Costs At a Glance</h2>
          <p className="section-subtitle">
            Higher pricing, inferior service, and multi-year contracts make Life Alert a less desirable option than Alert1.
          </p>
          
          <div className="comparison-table-wrapper">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th></th>
                  <th className="highlight-col">Alert1</th>
                  <th>Life Alert</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Monthly Fee Starting At</strong></td>
                  <td className="highlight-col">$19.99</td>
                  <td>$69.90</td>
                </tr>
                <tr>
                  <td><strong>Activation Fee</strong></td>
                  <td className="highlight-col">$0.00-$19.99</td>
                  <td>$0.00-$95.00</td>
                </tr>
                <tr>
                  <td><strong>Length of Agreement</strong></td>
                  <td className="highlight-col">1 month-1 year</td>
                  <td>3 years</td>
                </tr>
                <tr>
                  <td><strong>Stay on the Line Until Help Arrives</strong></td>
                  <td className="highlight-col">✓</td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td><strong>Pricing & Terms Posted Online</strong></td>
                  <td className="highlight-col">✓</td>
                  <td>✗</td>
                </tr>
                <tr>
                  <td><strong>Easy to Order Online</strong></td>
                  <td className="highlight-col">✓</td>
                  <td>✗</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="table-footnote">
            <sup>1</sup>Disclaimer: All information, prices, and data was last verified in December 2022. 
            To report any incorrect or outdated information, please get in touch with us. For more information 
            on Alert1's features and costs, check out our <a href="/pricing">pricing page</a>. To compare Alert1 
            to other medical alert systems, visit our Comparison Hub.
          </div>
        </div>
      </section>

      {/* 5 Reasons Section */}
      <section className="reasons-section">
        <div className="container">
          <h2>Why Choose Alert1 Over Life Alert?</h2>
          <p className="section-intro">
            More than 234,000 families nationwide have relied on Alert1 to stay safe and live better.
          </p>
          
          <h3>5 Reasons Why Alert1 is the Best Life Alert Alternative</h3>
          
          <div className="reasons-list">
            <div className="reason-item">
              <div className="reason-number">1</div>
              <div className="reason-content">
                <h4>Cost</h4>
                <p>
                  Life Alert Cost is usually higher than Alert1. Call to confirm. We have systems to meet every need, 
                  lifestyle, and budget (invaluable protection at affordable pricing)
                </p>
              </div>
            </div>

            <div className="reason-item">
              <div className="reason-number">2</div>
              <div className="reason-content">
                <h4>Support</h4>
                <p>
                  Once a member sends an alert, our emergency response team stays on the line with them until help 
                  arrives (not all companies do this)
                </p>
              </div>
            </div>

            <div className="reason-item">
              <div className="reason-number">3</div>
              <div className="reason-content">
                <h4>Transparency</h4>
                <p>
                  Alert1 does not charge our members for "false alarms" or multiple emergency button-pushes 
                  (some other companies do)
                </p>
              </div>
            </div>

            <div className="reason-item">
              <div className="reason-number">4</div>
              <div className="reason-content">
                <h4>Flexibility</h4>
                <p>
                  We provide monthly service plan options so no one is "locked in," as well as discounted annual plans 
                  (some of our competitors only offer multi-year plans). Alert1 has no long term contracts.
                </p>
              </div>
            </div>

            <div className="reason-item">
              <div className="reason-number">5</div>
              <div className="reason-content">
                <h4>Service</h4>
                <p>
                  Our emergency Monitoring Centers are staffed by trained agents who are available 24/7/365 
                  (professional responders are always standing by)
                </p>
              </div>
            </div>
          </div>

          <div className="cta-center">
            <a href="/medical-alerts" className="btn btn-primary">TRY US TODAY</a>
          </div>
        </div>
      </section>

      {/* Best Alternative Section */}
      <section className="alternative-section">
        <div className="container">
          <h2>Alert1: The Best Life Alert Alternative</h2>
          <p>
            Life Alert is often quoted for their well-remembered television slogan, "I've fallen and I can't get up."™ 
            Without high-priced commercial air time to finance, Alert1 is able to provide more affordable solutions that 
            match and even exceed the features of Life Alert's medical alert systems. Another important difference between 
            the two companies is that Life Alert requires 3 year contracts from its members, while Alert1 offers flexible 
            plan options that range from just one month at a time to 1 year-- with no contracts. We have found that some 
            members enjoy our annual plans as they offer the greatest savings, while others prefer paying one month at a time. 
            No matter which plan is selected, members are never "locked in" and may choose to cancel at any time. Life Alert 
            systems can be more than three times more expensive than those of Alert1. Choosing Alert1 as an alternative to 
            Life Alert could save you more than $140 in the first year alone!<sup>1</sup>
          </p>
          <p>
            If you've ever visited Life Alert's website, you will notice that there is no pricing information displayed. 
            In contrast, Alert1's website is transparent on our pricing and quite easy to use. Simply choose whether you 
            want a medical alert that works in the home or on the go, and decide if you want to add fall detection (we 
            highly recommend this feature to all of our members for optimal safety and protection). That's all there is to it! 
            You can easily order online or give us a call and we can answer any questions and assist you in securing your 
            new device. We also ship our systems via FedEx Express 2 Day Shipping. Because our operations are organized and 
            efficient, we are able to ship 85% of our new orders on the same day they are placed. These are just some of the 
            many value-added benefits that make Alert1 the ideal choice when selecting a medical alert service provider.
          </p>
          <div className="cta-center">
            <a href="/medical-alerts" className="btn btn-primary">VIEW SYSTEMS</a>
          </div>
        </div>
      </section>

      {/* Value Section */}
      <section className="value-section">
        <div className="container">
          <h2>Can Life Alert Match Alert1's Value?</h2>
          <p className="section-intro">
            Alert1 offers the best value in the industry. When it comes to selecting a personal button alarm for you 
            or your loved one, what really matters most? Is it fancy advertising and "name recognition," or is it 
            world-class service, affordability, important features, and VALUE?
          </p>

          <div className="value-points">
            <div className="value-point">
              <h3>Fair and Transparent Pricing</h3>
              <p>
                Alert1's pricing starts at just $19.99 per month—significantly lower pricing than that of Life Alert. 
                Additionally, depending on one's level of service with Life Alert, you may be charged hefty programming 
                and installation fees. Alert1 discloses all of our pricing up front. There are no hidden fees or charges. 
                We offer flexible plans ranging from monthly to annual subscriptions so our members can choose the right 
                combination of price and features that works best for them.
              </p>
            </div>

            <div className="value-point">
              <h3>Flexible Plan Options</h3>
              <p>
                Unlike Life Alert, Alert1 does not require long term contracts; any user may cancel service at any time by returning the equipment. If you agree to a contract of a specified length (e.g. annual) for a discount, you may cancel early but Alert1 does not offer refunds except in the case of death or a move to a certified nursing home or assisted living facility where Alert1 service is no longer supported or allowed. Life Alert's contract term is a lengthy 3 years.
              </p>
            </div>

            <div className="value-point">
              <h3>World Class Service</h3>
              <p>
                Alert1 understands that service is everything. While you are welcome to peruse our informative website, 
                we'd be happy to have one of our knowledgeable and friendly new member specialists discuss with you which 
                medical alert would work best for your lifestyle, needs, and budget.
              </p>
            </div>

            <div className="value-point">
              <h3>Comfort and Care When You Need it Most</h3>
              <p>
                Once an alert is sent to our trained agents, Alert1 remains on the line with members until help arrives. 
                We understand that traumatic experiences like falls and accidents may be frightening, confusing, and 
                disorienting, and we make a promise to all of our members that they will never face an emergency alone.
              </p>
            </div>
          </div>

          <div className="cta-center">
            <a href="/medical-alerts" className="btn btn-primary">VIEW SYSTEMS</a>
          </div>
        </div>
      </section>

      {/* Customer Reviews Section */}
      <section className="compare-reviews-section">
        <div className="container">
          <h2>What Our Customers Say</h2>
          <p className="section-intro">
            Don't just take our word for it - see what real Alert1 customers have to say about our service.
          </p>

          <div className="reviews-grid">
            {reviews.length > 0 ? (
              reviews.map((review) => (
                <Link 
                  to={`/reviews#review-${review.id}`} 
                  key={review.id} 
                  className="review-card-link"
                >
                  <div className="review-card">
                    <div className="review-stars">{renderStars(review.rating)}</div>
                    <h4 className="review-title">{review.title}</h4>
                    <p className="review-text">"{review.text}"</p>
                    <p className="review-author">— {review.author}</p>
                  </div>
                </Link>
              ))
            ) : (
              <>
                <div className="review-card">
                  <div className="review-stars">★★★★★</div>
                  <p className="review-text">
                    "I switched from Life Alert to Alert1 and I'm so glad I did. The service is just as good, 
                    if not better, and I'm saving over $30 a month. No long contract either!"
                  </p>
                  <p className="review-author">— Margaret T., Florida</p>
                </div>
                <div className="review-card">
                  <div className="review-stars">★★★★★</div>
                  <p className="review-text">
                    "Alert1 has been wonderful. They stay on the line until help arrives, which gives me 
                    peace of mind. Much more affordable than other companies I looked at."
                  </p>
                  <p className="review-author">— Robert K., Texas</p>
                </div>
                <div className="review-card">
                  <div className="review-stars">★★★★★</div>
                  <p className="review-text">
                    "Best decision I made for my mother. Easy to set up, great customer service, and the 
                    monthly cost is very reasonable. Highly recommend Alert1 over Life Alert."
                  </p>
                  <p className="review-author">— Linda M., California</p>
                </div>
              </>
            )}
          </div>

          <div className="review-cta">
            <Link to="/reviews" className="btn-secondary">See All Reviews</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ComparePage;

